import { Controller } from "@hotwired/stimulus"

// Handles AJAX form/button submissions within the scene detail modal,
// keeping the modal open instead of causing full-page reloads.
//
// Two submission modes:
//   ajaxSave  — submit form via fetch, show "Saved" flash (settings, padding, trim)
//   ajaxAction — submit via fetch, then reload page with modal auto-reopen
//                (select/delete generation, add/delete filler)
export default class extends Controller {
  static targets = ["status"]
  static values = { modalId: String }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  // --- ajaxSave: form submission that stays in-place ---

  async ajaxSave(event) {
    event.preventDefault()
    const form = event.target.closest("form") || event.target
    if (!(form instanceof HTMLFormElement)) return

    const btn = form.querySelector('[type="submit"]')
    if (btn) {
      btn.disabled = true
      btn.dataset.originalText = btn.value || btn.textContent
    }

    try {
      const formData = new FormData(form)
      const response = await fetch(form.action + ".json", {
        method: form.method?.toUpperCase() === "GET" ? "GET" : "POST",
        headers: { "X-CSRF-Token": this.csrfToken },
        body: formData,
      })

      const data = await response.json()
      if (data.success) {
        this.showStatus("Saved", "text-green-600")
      } else {
        this.showStatus(data.error || "Save failed", "text-red-600")
      }
    } catch (e) {
      console.error("ajaxSave failed:", e)
      this.showStatus("Save failed", "text-red-600")
    } finally {
      if (btn) {
        btn.disabled = false
      }
    }
  }

  // --- ajaxAction: button/form action that reloads the modal ---

  async ajaxAction(event) {
    event.preventDefault()
    event.stopPropagation()

    // Find the form — button_to generates a form wrapping the button
    const btn = event.currentTarget
    const form = btn.closest("form")
    if (!form) return

    // Confirmation check (data-turbo-confirm)
    const confirmMsg = btn.dataset.turboConfirm || form.dataset.turboConfirm
    if (confirmMsg && !window.confirm(confirmMsg)) return

    btn.disabled = true

    try {
      const formData = new FormData(form)

      // Determine HTTP method from hidden _method field or form method
      const methodField = form.querySelector('input[name="_method"]')
      const httpMethod = methodField?.value?.toUpperCase() || form.method?.toUpperCase() || "POST"

      const response = await fetch(form.action + ".json", {
        method: httpMethod === "GET" ? "GET" : "POST",
        headers: { "X-CSRF-Token": this.csrfToken },
        body: formData,
      })

      const data = await response.json()
      if (data.success) {
        this.reloadWithModal()
      } else {
        this.showStatus(data.error || "Action failed", "text-red-600")
        btn.disabled = false
      }
    } catch (e) {
      console.error("ajaxAction failed:", e)
      this.showStatus("Action failed", "text-red-600")
      btn.disabled = false
    }
  }

  // --- Helpers ---

  reloadWithModal() {
    if (this.modalIdValue) {
      sessionStorage.setItem("reopenModal", this.modalIdValue)
    }
    window.location.reload()
  }

  showStatus(message, colorClass) {
    if (!this.hasStatusTarget) return
    const el = this.statusTarget
    el.textContent = message
    el.className = `text-xs font-medium ${colorClass} transition-opacity`
    setTimeout(() => {
      el.style.opacity = "0"
      setTimeout(() => { el.textContent = ""; el.style.opacity = "1" }, 300)
    }, 2500)
  }
}
